import {
	Directive,
	EventEmitter,
	Input,
	OnChanges,
	Output,
	SimpleChanges,
} from "@angular/core";
import {
	AbstractControl,
	FormGroup,
	FormGroupDirective,
	ValidationErrors,
} from "@angular/forms";
import { MatFormFieldAppearance } from "@angular/material/form-field";
import { OnDestroyedDirective } from "app/shared/components/on-destroyed-component";
import {
	FormFieldEvent,
	FormFieldEventType,
	KeyRestrictionsTypes,
} from "./form-field-enums";
import { FormFieldType } from "./form-field.model";
import { UtilityService } from "@dodo/services/utility.service";
import {
	alphaKeysOnly,
	alphaNumericKeysOnly,
	numericKeysOnly,
	numericKeysOnlyWithDecimalPoint,
} from "app/shared";
import { takeUntil } from "rxjs/operators";

@Directive()
export abstract class FormFieldDirective
	extends OnDestroyedDirective
	implements OnChanges {
	// Private Variables
	private keyRestrictionsFunctionMap: {
		[key: string]: (event: any) => boolean;
	} = {
			"numeric-only": numericKeysOnly,
			"numeric-with-decimal-point": numericKeysOnlyWithDecimalPoint,
			"alpha-numeric": alphaNumericKeysOnly,
			"alpha-only": alphaKeysOnly,
		};
	// Public Variables
	public appearance: MatFormFieldAppearance = "fill";
	public invalidImage = "/assets/img/icons/exclamation-large.svg";
	public validImage = "/assets/img/icons/icon-tick-green.svg";
	public formGroupDirective: FormGroupDirective;
	public keyRestrictionFunction: any;
	public formGroup: FormGroup;
	public control: AbstractControl;
	public errorMessage: string;
	public type: FormFieldType;
	public touched: boolean;
	public hasError: boolean;
	// Input Variables
	@Input() label: string;
	@Input() placeholder: string;
	@Input() hint: string;
	@Input() required: boolean;
	@Input() disabled: boolean;
	@Input() maxLength = 50;
	@Input() minLength = 0;
	@Input() controlName: string;
	@Input() inputType = "text";
	@Input() parent: any;
	@Input() disablePaste = false;
	@Input() conditionalValidation = false;
	@Input() validationFn: (control: AbstractControl) => any;
	@Input() errorFn: (
		errors: ValidationErrors,
		label?: string,
		control?: AbstractControl
	) => string;
	options: any;
	@Input() set keyRestrictions(val: KeyRestrictionsTypes) {
		this.keyRestrictionFunction =
			this.keyRestrictionsFunctionMap?.[val] || alphaKeysOnly;
		this._keyRestrictionsType = val;
	}
	public _keyRestrictionsType: KeyRestrictionsTypes;

	public applyKeyRestriction(event: any): void {
		if (!this._keyRestrictionsType || !this.control) return;
		
		if (this._keyRestrictionsType === 'alpha-only') {
			const cleaned = this.control.value?.replace(/[^a-zA-Z\s'-]/g, '') || '';
			if (cleaned !== this.control.value) {
				this.control.setValue(cleaned);
			}
		} else if (this._keyRestrictionsType === 'alpha-numeric') {
			const cleaned = this.control.value?.replace(/[^a-zA-Z0-9]/g, '') || '';
			if (cleaned !== this.control.value) {
				this.control.setValue(cleaned);
			}
		} else if (this._keyRestrictionsType === 'numeric-only') {
			const cleaned = this.control.value?.replace(/[^0-9]/g, '') || '';
			if (cleaned !== this.control.value) {
				this.control.setValue(cleaned);
			}
		}
	}
	// Output Variables
	@Output() event = new EventEmitter<FormFieldEvent>();

	constructor(formGroupDirective: FormGroupDirective) {
		super();
		this.formGroupDirective = formGroupDirective;
	}

	ngOnChanges(changes: SimpleChanges): void {
		if ("controlName" in changes) {
			if (this.formGroupDirective) {
				this.formGroup = this.formGroupDirective.form;
				this.control =
					this.formGroup.controls[changes.controlName.currentValue];
				this.setValidators();
				// Subscribe to status changes to update error state reactively
				this.control?.statusChanges
					?.pipe(takeUntil(this.unsubscribeAll))
					.subscribe(() => {
						if (this.touched) {
							this.checkErrors(this.control?.value);
						}
					});
			}
		} else if (
			this.conditionalValidation &&
			changes?.required?.currentValue === false
		) {
			this.checkErrors();
		}
	}

	public inputChanged(): any {
		this.checkErrors(this.control?.value);
			this.event.emit({
				type: FormFieldEventType.INPUT_CHANGED,
			} as FormFieldEvent);
	}
	public preventPaste(event: Event): void {
		if (this.disablePaste) {
			event.preventDefault();
		}
	}
	public checkErrors(value?: any): void {
		if (
			this.type === FormFieldType.INPUT ||
			this.type === FormFieldType.MASKED ||
			this.type === FormFieldType.MOMENT
		) {
			if (this.errorFn) {
				const error = this.errorFn(this.control.errors, this.parent);
				if (error) {
					this.hasError = true;
					this.errorMessage = error;
					return;
				}
			}
			if (this.control?.hasError("required")) {
				this.hasError = true;
				this.errorMessage = `${this.label} is required`;
				return;
			} else if (this.control?.hasError("error")) {
				this.hasError = true;
				this.errorMessage = this.control.errors.error;
				return;
			} else if (
				this.required &&
				this.touched &&
				this.control.value?.length === 0
			) {
				this.hasError = true;
				this.errorMessage = `${this.label} is required`;
				return;
			} else if (
				this.required &&
				this.touched &&
				this.control.value?.length < this.minLength
			) {
				this.hasError = true;
				this.errorMessage = `Invalid ${this.label}`;
			}
		} else if (this.type === FormFieldType.SELECT) {
			if (this.required && this.touched && !value) {
				this.hasError = true;
				this.errorMessage = "Please select an option";
				return;
			}
		} else if (this.type === FormFieldType.ADDRESS) {
			if (this.control?.hasError("required")) {
				this.hasError = true;
				this.errorMessage = `${this.label} is required`;
				return;
			}
		}

		this.hasError = false;
	}

	public cleanMaskValue(val: string): string {
		return UtilityService.cleanMaskedValue(val);
	}

	private setValidators(): any {
		if (this.validationFn) {
			this.control.setValidators(this.validationFn);
		}
	}
	get valid(): boolean | undefined {
		return this.control
			? this.control.dirty &&
			!this.control.errors &&
			!this.control.pending &&
			!!this.control.value
			: undefined;
	}

	get image(): string {
		return this.hasError ? this.invalidImage : this.validImage;
	}

	get showImage(): boolean {
		return this.valid || this.hasError;
	}
}
